/**
 * Reads a connected input's bytes on the host. The shader sandbox has an opaque
 * origin and cannot fetch the host's URLs (blob:, the desktop engine's library,
 * signed storage links), so every input is read here and handed over as data.
 */

export interface FetchMediaOptions {
  signal?: AbortSignal
  /** Refuse anything larger, before downloading it when the server says how big it is. */
  maxBytes?: number
}

const MB = 1024 * 1024

export function formatMegabytes(bytes: number): string {
  return `${Math.round(bytes / MB)} MB`
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException ? error.name === 'AbortError' : error instanceof Error && error.name === 'AbortError'
}

export function abortError(): DOMException {
  return new DOMException('The input changed before it finished loading.', 'AbortError')
}

export function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw abortError()
}

/** Fetch a URL (http, blob or data) as a Blob, with readable errors and an optional size cap. */
export async function fetchMediaBlob(url: string, { signal, maxBytes }: FetchMediaOptions = {}): Promise<Blob> {
  let response: Response
  try {
    response = await fetch(url, { signal })
  } catch (error) {
    if (isAbortError(error)) throw error
    throw new Error('The file could not be downloaded. Its server may not allow it to be read from here.')
  }
  if (!response.ok) throw new Error(`The file could not be downloaded (HTTP ${response.status}).`)
  const declared = Number(response.headers.get('content-length'))
  if (maxBytes && Number.isFinite(declared) && declared > maxBytes) throw tooLarge(declared, maxBytes)
  const blob = await response.blob()
  if (maxBytes && blob.size > maxBytes) throw tooLarge(blob.size, maxBytes)
  return blob
}

function tooLarge(size: number, limit: number): Error {
  return new Error(`The file is too large (${formatMegabytes(size)}; the limit is ${formatMegabytes(limit)}).`)
}

/** Lowercased extension of a URL's path, without the dot ('' when there is none). */
export function urlExtension(url: string): string {
  if (url.startsWith('data:') || url.startsWith('blob:')) return ''
  const path = url.split(/[?#]/)[0]
  const dot = path.lastIndexOf('.')
  return dot > path.lastIndexOf('/') ? path.slice(dot + 1).toLowerCase() : ''
}
